import {
  Search,
  Replace,
  Wrench,
  ActivitySquare,
  Sparkles,
  BadgeCheck,
  ShieldCheck,
  Gauge,
  FileCheck2,
  PiggyBank,
  Activity,
  TrendingDown 
} from "lucide-react";
import { SectionHeader } from "./Section";
import oilGas from "@/assets/oil-gas.jpg";

const STAGES = [
  {
    icon: Search,
    title: "Strategic Disassembly & Diagnostic Review",
    desc: "Controlled teardown, root-cause analysis and condition mapping of every critical component.",
  },
  {
    icon: Replace,
    title: "Component Renewal & Replacement",
    desc: "OEM-grade replacement of worn, fatigued or non-conforming parts to original specification.",
  },
  {
    icon: Wrench,
    title: "Precision Reassembly",
    desc: "Reassembly under torque-controlled, witness-tested workshop procedures.",
  },
  {
    icon: ActivitySquare,
    title: "Performance Validation",
    desc: "Functional, load and dynamic testing to validate restored performance envelope.",
  },
  {
    icon: Sparkles,
    title: "Premium Refurbishment Enhancements",
    desc: "Optional upgrades — coatings, controls, monitoring — to exceed original capability.",
  },
  {
    icon: BadgeCheck,
    title: "Certification & Compliance",
    desc: "Re-issue of certificates aligned with API, DNV, ISO and operator requirements.",
  },
];

const BENEFITS = [
  {
    icon: ShieldCheck,
    label: "Safety",
    description:
      "Protecting people, assets, and operations through rigorous inspections and risk prevention.",
  },
  {
    icon: Gauge,
    label: "Reliability",
    description:
      "Ensuring equipment performs consistently and efficiently under the most demanding conditions.",
  },
  {
    icon: FileCheck2,
    label: "Compliance",
    description:
      "Meeting international standards and regulatory requirements with certified documentation.",
  },
  {
    icon: TrendingDown,
    label: "Cost Optimization",
    description:
      "Reducing downtime, extending asset life, and minimizing unnecessary maintenance expenses.",
  },
  {
    icon: Activity,
    label: "Operational Continuity",
    description:
      "Maintaining smooth operations through proactive inspections and preventive solutions.",
  },
];

export function Overhauling() {
  return (
    <section className="relative section-y overflow-hidden bg-navy-deep text-white">
      <img
        src={oilGas}
        alt="Oil and gas facility at night"
        width={1280}
        height={800}
        loading="lazy"
        className="absolute inset-0 -z-10 h-full w-full object-cover opacity-15"
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-navy-deep via-navy-deep/95 to-navy-deep" />

      <div className="container-x">
        <SectionHeader
          eyebrow="The Overhauling Journey"
          title="Six engineered stages. One certified outcome."
          intro="Our structured overhaul process restores equipment to better-than-new performance while documenting every step for full regulatory traceability."
          invert
        />

        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {STAGES.map((s, i) => (
            <div
              key={s.title}
              className="group relative rounded-xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur transition-all hover:border-orange hover:bg-white/[0.07]"
            >
              <div className="flex items-start justify-between">
                <span className="grid h-12 w-12 place-items-center rounded-md bg-orange text-white">
                  <s.icon className="h-6 w-6" />
                </span>
                <span className="font-mono text-3xl font-black text-white/15 transition-colors group-hover:text-orange/40">
                  0{i + 1}
                </span>
              </div>
              <div className="mt-5 text-xs font-bold uppercase tracking-widest text-orange">
                Stage {i + 1}
              </div>
              <h3 className="mt-2 text-lg font-bold text-white">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/70">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-20">
          <div className="mx-auto max-w-3xl text-center">
            <div className="text-4xl font-bold uppercase tracking-[0.3em] text-orange ">
              Delivered Benefits
            </div>

            <h3 className="mt-3 text-3xl font-bold text-white">
              Why Our Overhauling Process Delivers Superior Results
            </h3>

            <p className="mt-4 text-base leading-relaxed text-white/70">
              Every overhaul is performed through a structured engineering workflow,
              ensuring maximum safety, compliance and long-term operational reliability.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {BENEFITS.map((b) => (
              <div
                key={b.label}
                className="group rounded-2xl border border-white/10 bg-white/[0.05] p-7 text-center transition-all duration-300 hover:-translate-y-2 hover:border-orange hover:bg-white/[0.08] hover:shadow-2xl"
              >
                <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-orange/10 text-orange transition-all duration-300 group-hover:bg-orange group-hover:text-white">
                  <b.icon className="h-8 w-8" />
                </div>

                <h4 className="mt-6 text-[15px] font-bold text-white">
                  {b.label}
                </h4>

                <div className="mx-auto mt-4 h-1 w-10 rounded-full bg-orange/40 transition-all duration-300 group-hover:w-16 group-hover:bg-orange" />

                <p className="mt-4 text-sm leading-relaxed text-white/65">
                   {b.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
